import { useQuery } from '@tanstack/react-query';

import React, { useState } from 'react';

import { Box, Divider, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';

import { useApiClient } from '@@common';
import { CopyToClipboardButton } from '@@components';

import { EnvironmentClient } from '../../../api/api';
import SidePanelDetail from '../../../components/Panels/SidePanelDetail';
import AddVersionDialog from './AddVersionDialog';
import AttributesList from './AttributesList';
import { CONTAINER_IMAGES_PANEL_COLLAPSED_WIDTH, CONTAINER_IMAGES_PANEL_DEFAULT_WIDTH } from './ContainerImagesPanel';
import SectionWithAction from './SectionWithAction';

const CONTAINER_IMAGE_DETAILS_PANEL_STORAGE_KEY = 'containerImageDetailsPanel.width';
const CONTAINER_IMAGE_DETAILS_PANEL_DEFAULT_WIDTH = 500;
const CONTAINER_IMAGE_DETAILS_PANEL_MIN_WIDTH = 360;
const CONTAINER_IMAGE_DETAILS_PANEL_MAX_WIDTH = 1000;

interface ContainerImageDetailsPanelProps {
	imageId: string;
	onClose: () => void;
	onVersionAdded?: () => void;
	listPanelCollapsed?: boolean;
	rightOffset?: number;
	top?: number;
}

export default function ContainerImageDetailsPanel({
	imageId,
	onClose,
	onVersionAdded,
	listPanelCollapsed = false,
	rightOffset = CONTAINER_IMAGES_PANEL_DEFAULT_WIDTH,
	top = 0,
}: ContainerImageDetailsPanelProps) {
	const client = useApiClient(EnvironmentClient) as EnvironmentClient;
	const [refreshKey, setRefreshKey] = useState(0);
	const [openAddVersionDialog, setOpenAddVersionDialog] = useState(false);
	const [panelWidth, setPanelWidth] = useState(CONTAINER_IMAGE_DETAILS_PANEL_DEFAULT_WIDTH);

	// Query to fetch container image details
	const {
		data: containerImage,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useQuery({
		queryKey: ['container-image-details', imageId, refreshKey],
		queryFn: async () => {
			return await client.containerImageGet(imageId);
		},
	});

	const handleRefresh = () => {
		setRefreshKey((prev) => prev + 1);
		refetch();
	};

	const handleAddVersion = () => {
		setOpenAddVersionDialog(true);
	};

	const handleVersionAdded = () => {
		setOpenAddVersionDialog(false);
		handleRefresh();
		onVersionAdded?.();
	};

	const handleCloseAddVersionDialog = () => {
		setOpenAddVersionDialog(false);
	};

	// Subtitle component with ID and copy button
	const subtitle = (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
			<Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1, flexShrink: 1 }}>
				ID: {imageId}
			</Typography>
			<CopyToClipboardButton value={imageId} size="small" iconSize={14} title="Copy ID to clipboard" edge={false} sx={{ p: 0.5 }} />
		</Box>
	);

	return (
		<>
			<SidePanelDetail
				title="Container Image Details"
				subtitle={subtitle}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error as Error | null}
				errorMessage="Failed to load container image details"
				onClose={onClose}
				onRefresh={handleRefresh}
				position="stacked"
				top={top}
				rightOffset={rightOffset}
				width={panelWidth}
				zIndex={1201}
				listPanelCollapsed={listPanelCollapsed}
				listPanelCollapsedWidth={CONTAINER_IMAGES_PANEL_COLLAPSED_WIDTH}
				resizeStorageKey={CONTAINER_IMAGE_DETAILS_PANEL_STORAGE_KEY}
				defaultWidth={CONTAINER_IMAGE_DETAILS_PANEL_DEFAULT_WIDTH}
				minWidth={CONTAINER_IMAGE_DETAILS_PANEL_MIN_WIDTH}
				maxWidth={CONTAINER_IMAGE_DETAILS_PANEL_MAX_WIDTH}
				setWidth={setPanelWidth}
			>
				{containerImage && (
					<>
						<Box sx={{ mb: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="body2" color="textSecondary">
									Name:
								</Typography>
								<Typography variant="body2" sx={{ fontWeight: 500 }}>
									{containerImage.name}
								</Typography>
							</Stack>

							<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="body2" color="textSecondary">
									Latest Version:
								</Typography>
								<Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
									{containerImage.latestVersion || '-'}
								</Typography>
							</Stack>
						</Box>

						{/* Environment Variables */}
						<AttributesList
							attributes={containerImage.environmentVariables}
							title="Environment Variables"
							emptyMessage="No environment variables defined for this container image"
						/>

						<Divider sx={{ my: 2 }} />
						{/* Versions List */}
						<SectionWithAction
							title="Versions"
							count={containerImage.versions?.length || 0}
							onAdd={handleAddVersion}
							addButtonTitle="Add version"
							emptyMessage="No versions found for this container image"
						>
							<List dense sx={{ py: 0 }}>
								{containerImage.versions.map((version, index) => (
									<ListItem
										key={index}
										sx={{
											px: 0,
											py: 1,
											borderBottom: index < containerImage.versions.length - 1 ? '1px solid #e0e0e0' : 'none',
										}}
									>
										<ListItemText
											primary={
												<Stack direction="row" spacing={1} alignItems="center">
													<Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
														{version}
													</Typography>
													<CopyToClipboardButton value={version} size="small" iconSize={14} title="Copy version to clipboard" edge={false} />
												</Stack>
											}
										/>
									</ListItem>
								))}
							</List>
						</SectionWithAction>
					</>
				)}
			</SidePanelDetail>

			{/* Add Version Dialog */}
			{containerImage && (
				<AddVersionDialog
					open={openAddVersionDialog}
					imageId={imageId}
					imageName={containerImage.name}
					onClose={handleCloseAddVersionDialog}
					onVersionAdded={handleVersionAdded}
				/>
			)}
		</>
	);
}
